import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { PlayerProfile } from '../domain/types';

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

export const cloudEnabled = Boolean(url && anonKey && !url.includes('your-project'));
export const supabase: SupabaseClient | null = cloudEnabled ? createClient(url!, anonKey!) : null;

export async function sendMagicLink(email: string): Promise<void> {
  if (!supabase) throw new Error('Cloud sign-in is not configured. Add the Supabase settings to enable accounts.');
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: { emailRedirectTo: `${window.location.origin}/auth/callback`, shouldCreateUser: true },
  });
  if (error) throw error;
}

export const signInWithEmail = sendMagicLink;

/**
 * Ends the account session on this device.
 *
 * The caller is responsible for what happens to saved progress after this:
 * sign-out is a confirmed product decision that clears the device's local copy
 * and returns the player to the landing page. See
 * `docs/decisions/DEC-006-sign-out.md`. An error is thrown rather than swallowed
 * so the screen can report that sign-out did not complete instead of appearing
 * to have worked.
 */
export async function signOut(): Promise<void> {
  if (!supabase) throw new Error('Cloud sign-out is not configured, so there is no session to end.');
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

/**
 * Whether the signed-in account is on the developer list.
 *
 * The list itself is never readable from the browser: a `security definer` function
 * in the database answers the question for the caller's own email, so the list
 * cannot be harvested even by a signed-in player. `false` when there is no cloud
 * configuration at all, because then there are no accounts to be a developer on.
 * An error is thrown rather than swallowed, so the caller can keep the testing
 * helpers off and report why instead of quieting them without explanation.
 */
export async function isDeveloperAccount(): Promise<boolean> {
  if (!supabase) return false;
  const { data, error } = await supabase.rpc('is_developer');
  if (error) throw error;
  return data === true;
}

/**
 * Writes the one row that belongs to the account, creating it if it is not there yet.
 *
 * Written as an update and then an insert, rather than as a single upsert. That is not
 * a style choice: an upsert is refused outright by this table's permissions.
 *
 * The browser is deliberately not allowed to change most of this row - the schema
 * grants UPDATE on `continent` and `updated_at` alone, so that a player cannot award
 * themselves GachaCards. An upsert cannot respect that, because the statement PostgREST
 * builds for it sets *every* column it was sent, including `user_id`, and the grant does
 * not cover `user_id`. Postgres checks those column privileges before running the
 * statement, so the write was refused every single time - whether or not a row already
 * existed, and so even the first save of a brand-new account. That refusal is what the
 * save notice was reporting. See `docs/decisions/DEC-012`.
 *
 * Only the two columns the client owns are ever written, so this stays within the
 * permission the schema intended and no grant has to be widened.
 */
async function writeProfileRow(userId: string, continent: string): Promise<void> {
  if (!supabase) return;
  // A fresh timestamp is taken per attempt rather than shared, so a retry after a race
  // does not write a time that has already passed.
  const update = () => supabase!.from('player_profiles')
    .update({ continent, updated_at: new Date().toISOString() })
    .eq('user_id', userId)
    // Selected so that "no rows came back" can be told from "a row was updated". Without
    // it the request reports success either way and the insert below would never run.
    .select('user_id');
  const written = await update();
  if (written.error) throw written.error;
  if (written.data?.length) return;

  const created = await supabase.from('player_profiles').insert({ user_id: userId, continent });
  if (!created.error) return;
  // 23505 is the row having been created by a save that overlapped this one. That row is
  // this account's own, so it is not a failure - but it may hold the other attempt's
  // region, so the update is made once more rather than left for the next save.
  if (created.error.code === '23505') {
    const writtenAgain = await update();
    if (writtenAgain.error) throw writtenAgain.error;
    return;
  }
  throw created.error;
}

/**
 * Writes the profile to the account, and returns the account it was written to.
 *
 * `null` means nothing was written. That is not the same as success: the caller has a
 * device copy that the account does not, which is exactly the state the save notice
 * exists to show, so it is reported rather than passed off as a save.
 */
export async function syncProfile(profile: PlayerProfile): Promise<string | null> {
  if (!supabase) return null;
  const { data: authData, error: authError } = await supabase.auth.getUser();
  // An error here means the account could not be reached, and that is exactly the
  // failure the save indicator exists to show. Passed over instead, an offline save
  // would look like a successful one: `getUser` reports an error and no user, so the
  // write below would be skipped and nothing would ever tell the player.
  if (authError) throw authError;
  const user = authData.user;
  if (!user) return null;

  await writeProfileRow(user.id, profile.continent);

  if (profile.garaimon.length) {
    const creaturesResult = await supabase.from('garaimon_instances').upsert(profile.garaimon.map((creature) => ({
      id: creature.id,
      user_id: user.id,
      species_id: creature.speciesId,
      nickname: creature.nickname || null,
      level: creature.level,
      xp: creature.xp,
      learned_ability_ids: creature.learnedAbilityIds,
      ai_profile: creature.aiProfile,
      updated_at: new Date().toISOString(),
    })));
    if (creaturesResult.error) throw creaturesResult.error;
  }
  return user.id;
}

export async function consumeGachaCard(battleId?: string): Promise<number | null> {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('consume_gacha_card', { p_battle: battleId ?? null });
  if (error) throw error;
  return data as number;
}

/**
 * Records a completed battle and returns the GachaCard reward it earned.
 *
 * `duplicate: true` in place of the counts means the battle was already recorded by
 * an earlier attempt whose reply never arrived. The caller must not credit the
 * reward again - the account has already paid it.
 */
export async function finalizeBattle(episode: PlayerProfile['episodes'][number]): Promise<{ cards: number; noCardStreak: number; duplicate?: boolean } | null> {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('finalize_battle', { p_episode: episode });
  if (error) throw error;
  return data as { cards: number; noCardStreak: number; duplicate?: boolean };
}

/**
 * The account's own copy of a player's progress.
 *
 * `profile` is null when the account has no saved row yet, which is the state of an
 * account that has never finished a battle - not an error, and not the same thing as
 * an account holding nothing after having been used.
 *
 * A failure to reach the account throws. This is the important difference from
 * reporting the same thing as an empty account: a caller that cannot tell "your
 * account has no progress" from "your account could not be read" will write a newer
 * device copy over an older account one with nobody asked, which is the fault the
 * sign-in save decision exists to prevent. See `src/domain/signInSave.ts`.
 *
 * `base` supplies the fields the account does not store - the recent-seed list is
 * local-only, because it only exists to avoid repeating a board.
 */
export interface AccountSave {
  /** The account the copy belongs to, so a caller can stamp the device copy with it. */
  accountId: string;
  /** The account's progress, or null when the account has no saved row yet. */
  profile: PlayerProfile | null;
}

export async function readAccountSave(base: PlayerProfile): Promise<AccountSave> {
  if (!supabase) throw new Error('No account service is configured.');
  const { data: authData, error: authError } = await supabase.auth.getUser();
  if (authError) throw authError;
  const user = authData.user;
  // No user with no error means the session has ended. There is no account to read,
  // and saying so is better than returning an empty one for the caller to trust.
  if (!user) throw new Error('The account session has ended.');
  const [profileResult, creatureResult, episodeResult] = await Promise.all([
    supabase.from('player_profiles').select('no_card_streak,continent,gacha_cards').eq('user_id', user.id).maybeSingle(),
    supabase.from('garaimon_instances').select('id,species_id,nickname,level,xp,learned_ability_ids,ai_profile').eq('user_id', user.id),
    supabase.from('battle_episodes').select('id,seed,simulation_version,policy_version,arena,participants,decision_events,outcome,end_reason,duration_ms').eq('user_id', user.id).order('created_at', { ascending: false }).limit(120),
  ]);
  if (profileResult.error) throw profileResult.error;
  if (creatureResult.error) throw creatureResult.error;
  if (episodeResult.error) throw episodeResult.error;
  const cloudProfile = profileResult.data;
  if (!cloudProfile) return { accountId: user.id, profile: null };
  const creatures = (creatureResult.data ?? []).map((row) => ({ id: row.id, speciesId: row.species_id, nickname: row.nickname ?? '', level: row.level, xp: row.xp, learnedAbilityIds: row.learned_ability_ids, aiProfile: row.ai_profile }));
  const episodes = (episodeResult.data ?? []).map((row) => ({ id: row.id, seed: Number(row.seed), simulationVersion: row.simulation_version, policyVersion: row.policy_version, arena: row.arena, participants: row.participants, events: row.decision_events, outcome: row.outcome, endReason: row.end_reason, durationMs: row.duration_ms }));
  return {
    accountId: user.id,
    profile: {
      ...base,
      noCardStreak: cloudProfile.no_card_streak,
      continent: cloudProfile.continent,
      gachaCards: cloudProfile.gacha_cards,
      garaimon: creatures,
      episodes,
    } as PlayerProfile,
  };
}
